import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DATASET_CONTROL_DATA_PARAM } from '../../../constants';
import {
  Dataset,
  GlobalLocationStateType,
  JSONObject,
} from '../../../types/types';
import { getDatasetUrlParams } from '../../../utils/getDatasetUrlParams';
import { translatedToast } from '../../../utils/helpers';
import { getDatasetControlDataStatus } from '../../../utils/datasetControl/getDatasetControlDataStatus';
import {
  DatasetControlDataEntry,
  getNextDatasetControlDataEntry,
} from '../../../utils/datasetControl/getNextDatasetControlDataEntry';
import { parseDatasetControlData } from '../../../utils/datasetControl/parseDatasetControlData';
import { updateLazyStateFromActiveFilters } from '../../DatasetFormDataContext/utils/updateLazyStateFromActiveFilters';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('datasetControl');

type UseDatasetControlDataParams = {
  dataset: Dataset | undefined;
  datasetId: string | undefined;
  // Filter definition state — needed to regenerate default lazyState filters.
  filter: any;
  activeFiltersRef: MutableRefObject<Record<string, unknown> | null>;
  setActiveFilters: Dispatch<SetStateAction<Record<string, unknown> | null>>;
  setLazyState: Dispatch<SetStateAction<Record<string, unknown>>>;
};

const buildSearchString = (params: URLSearchParams) => {
  const paramsString = params.toString();
  return paramsString ? `?${paramsString}` : '';
};

// Reads the `data` query param that a `dataset` control puts into the URL.
// The parsed object goes into API requests as `datasetControl` —
// the frontend never interprets it, backend apiMethod templates do.
//
// The data survives navigation inside the dataset (the param is put back into
// the URL), including a form round-trip, and is cleared when the user leaves:
// another dataset or a menu click.
//
// The ref is filled already during render (not in an effect) so the very
// first data fetch after opening the dataset can read it.
export const useDatasetControlData = ({
  dataset,
  datasetId,
  filter,
  activeFiltersRef,
  setActiveFilters,
  setLazyState,
}: UseDatasetControlDataParams) => {
  const [datasetControl, setDatasetControl] = useState<JSONObject | null>(null);

  const datasetControlRef = useRef<DatasetControlDataEntry | null>(null);
  // `${datasetId}|${raw}` of the last data whose arrival cleared user
  // filters — a restored/re-rendered identical param must not re-clear them.
  const lastAppliedKeyRef = useRef<string | null>(null);
  // Raw value already stripped as invalid — strip + toast fire once per
  // invalid value, not per effect run.
  const lastStrippedRawRef = useRef<string | null>(null);

  const history = useHistory();
  const {
    pathname,
    search,
    state: locationState,
  } = useLocation<GlobalLocationStateType>();

  const parsed = useMemo(() => parseDatasetControlData(search), [search]);
  const routeDatasetId = getDatasetUrlParams(pathname).datasetId;
  const status = useMemo(
    () =>
      getDatasetControlDataStatus(parsed, routeDatasetId, datasetId, dataset),
    [parsed, routeDatasetId, datasetId, dataset]
  );

  // Render-time ref maintenance
  datasetControlRef.current = getNextDatasetControlDataEntry(
    datasetControlRef.current,
    {
      status,
      parsed,
      routeDatasetId,
      datasetId,
    }
  );

  // Manual cancellation — menu clicks. When the click targets the dataset the
  // data belongs to, no dataset switch will refetch, so force one through a
  // fresh activeFilters reference (the fetch-trigger effect picks it up).
  const clearDatasetControl = useCallback(
    (targetDatasetId?: string) => {
      if (!datasetControlRef.current) {
        return;
      }
      const clearedDatasetId = datasetControlRef.current.datasetId;

      /** @debugFeature datasetControl Logs a manual clear of dataset control data (menu click): the owning dataset and whether a forced refetch follows. */
      log('dataset control data cleared', () => ({
        clearedDatasetId,
        targetDatasetId,
        forcesRefetch: Boolean(
          targetDatasetId && targetDatasetId === clearedDatasetId
        ),
      }));

      datasetControlRef.current = null;
      lastAppliedKeyRef.current = null;
      setDatasetControl(null);
      if (targetDatasetId && targetDatasetId === clearedDatasetId) {
        // Same content, new object reference — wakes up the fetch-trigger
        // effect (deps: [activeFilters]) so the grid reloads without the
        // cleared data. `null` stays null: dataset not initialized yet,
        // nothing to reload.
        setActiveFilters((previous) => (previous ? { ...previous } : previous));
      }
    },
    [setActiveFilters]
  );

  // history.replace keeping pathname and location state — guards stash
  // one-shot flags in the state, so it must survive every replace.
  const replaceSearchParams = (params: URLSearchParams) => {
    history.replace({
      pathname,
      search: buildSearchString(params),
      state: locationState,
    });
  };

  // Newly arrived valid data: expose it and reset the user filters it must
  // replace. `null` activeFilters means a fresh dataset entry — nothing to
  // clear, the initial fetch reads the ref directly. lastAppliedKeyRef makes
  // this once-per-data: a restored identical param must not re-clear filters
  // the user set afterwards.
  const applyNewData = (entry: DatasetControlDataEntry) => {
    const key = `${entry.datasetId}|${entry.raw}`;
    if (lastAppliedKeyRef.current === key) {
      return;
    }
    lastAppliedKeyRef.current = key;

    /** @debugFeature datasetControl Logs newly applied dataset control data from the ?data url param, and whether it reset the user's filters. */
    log('dataset control data applied', () => ({
      datasetId: entry.datasetId,
      data: entry.data,
      resetsUserFilters: activeFiltersRef.current !== null,
    }));

    setDatasetControl(entry.data);

    if (activeFiltersRef.current !== null) {
      activeFiltersRef.current = {};
      setActiveFilters({});
      setLazyState((oldLazyState) =>
        updateLazyStateFromActiveFilters(oldLazyState, null, dataset, filter)
      );
    }
  };

  // The render-time reducer dropped the entry (form entry, dataset switch) —
  // mirror the clear into the exposed state.
  const mirrorClearedData = () => {
    if (datasetControl !== null) {
      lastAppliedKeyRef.current = null;
      setDatasetControl(null);
    }
  };

  // Invalid param: strip it from the URL and tell the user what was wrong
  // with it. Edge-triggered by lastStrippedRawRef — once per invalid value,
  // not per effect run.
  const stripInvalidParam = () => {
    const invalidRaw = parsed ? parsed.raw : null;
    if (lastStrippedRawRef.current === invalidRaw) {
      return;
    }
    lastStrippedRawRef.current = invalidRaw;
    const params = new URLSearchParams(search);
    params.delete(DATASET_CONTROL_DATA_PARAM);
    replaceSearchParams(params);

    const reason = parsed && 'malformed' in parsed ? parsed.reason : undefined;

    /** @debugFeature datasetControl Logs an invalid ?data url param stripped from the url, with the raw value and the rejection reason. */
    log('invalid dataset control param stripped', () => ({
      raw: invalidRaw,
      reason: reason ?? 'wrongDataset',
    }));
    if (reason === 'unparseable') {
      translatedToast('toasts.urlDataUnparseable');
      return;
    }
    if (reason === 'badShape') {
      translatedToast('toasts.urlDataBadShape');
      return;
    }
    translatedToast('toasts.invalidUrlData');
  };

  // In-dataset navigation pushes bare pathnames — reinstate the param so the
  // URL stays the source of truth (F5 anywhere keeps the data, incl.
  // dataset-scoped form routes). Never touches other datasets.
  const restoreMissingParam = (entry: DatasetControlDataEntry) => {
    if (getDatasetUrlParams(pathname).datasetId !== entry.datasetId) {
      return;
    }
    const params = new URLSearchParams(search);
    if (params.get(DATASET_CONTROL_DATA_PARAM) !== null) {
      return;
    }
    params.set(DATASET_CONTROL_DATA_PARAM, entry.raw);
    replaceSearchParams(params);
  };

  // Single location-sync pass; the branch order matters.
  useEffect(() => {
    const entry = datasetControlRef.current;

    if (status === 'valid' && entry) {
      applyNewData(entry);
    }

    if (!entry) {
      mirrorClearedData();
    }

    if (status === 'invalid') {
      stripInvalidParam();
      return;
    }

    lastStrippedRawRef.current = null;

    if (entry) {
      restoreMissingParam(entry);
    }
  }, [pathname, search, datasetId, status, datasetControl]);

  return { datasetControl, datasetControlRef, clearDatasetControl };
};
